import * as atlas from 'azure-maps-control';
import { IAzureMapOptions } from 'react-azure-maps';
import { InfoboxProps, MapEventHandlerProps, MapProps, PolygonProps, PolylineProps, PushpinProps } from '../../../types/MapModels';
/**
 * Utility type for Azure Maps coordinates [longitude, latitude]
 */
export type AzureCoordinatePair = [number, number];

/**
 * Converts the shared coordinate format [latitude, longitude] to Azure Maps format [longitude, latitude].
 */
export function convertCoordinates(coordinates: CoordinatePair): AzureCoordinatePair {
    const [lat, lon] = coordinates;
    return [lon, lat];
}

/**
 * Converts an array of shared coordinates to Azure Maps format.
 */
export function convertCoordinatesArray(coordinates: CoordinatePair[]): AzureCoordinatePair[] {
    return coordinates.map(convertCoordinates);
}

/**
 * Converts nested shared coordinates to Azure Maps format for polygons.
 */
export function convertCoordinatesArrays(rings: CoordinatePair[][]): AzureCoordinatePair[][] {
    return rings.map(convertCoordinatesArray);
}

/**
 * Converts shared map props to Azure Maps options.
 */
export function convertMapProps(mapProps?: MapProps): IAzureMapOptions {
    const azureMapOptions: IAzureMapOptions = {
        center: [-95.382669, 29.757224], // Default center (lon, lat)
        zoom: 10,
        authOptions: {
            authType: atlas.AuthenticationType.subscriptionKey,
            subscriptionKey: config.AzureMapKey,
        },
    };

    if (!mapProps) {
        return azureMapOptions;
    }

    if (mapProps.center) {
        azureMapOptions.center = convertCoordinates(mapProps.center);
    }

    if (mapProps.options?.zoom) {
        azureMapOptions.zoom = mapProps.options.zoom;
    }

    return azureMapOptions;
}

/**
 * Converts shared map event handlers to Azure Maps event handlers.
 */
export function convertMapEventHandlers(eventHandlers?: MapEventHandlerProps[]) {
    if (!eventHandlers || eventHandlers.length === 0) {
        return {};
    }

    const azureEvents: Record<string, any> = {};

    eventHandlers.forEach(handler => {
        const eventName = handler.event;

        azureEvents[eventName] = (e: any) => {
            // Provide minimal compatibility shape
            const loc = e?.position || e?.pixel || e?.shape?.data?.geometry?.coordinates;
            const args = {
                ...e,
                position: loc,
                eventName,
                originalEvent: e?.originalEvent ?? e,
                target: e?.target,
                getX: e?.getX,
                getY: e?.getY,
            };
            handler.callback(args as any);
        };
    });

    return azureEvents;
}

/**
 * Converts shared pushpin props to Azure Maps features.
 */
export function convertPushpinsToAzure(pushpins: PushpinProps[] = []): atlas.data.Feature<atlas.data.Point, any>[] {
    return pushpins.map((pushpin) => {
        const coordinates = convertCoordinates(pushpin.location);

        const properties: any = {
            ...pushpin.options,
        };

        if (pushpin.eventHandlers && pushpin.eventHandlers.length > 0) {
            pushpin.eventHandlers.forEach(handler => {
                const key = `on${handler.event}`;
                properties[key] = handler.callback;
            });
        }

        return new atlas.data.Feature(
            new atlas.data.Point(coordinates),
            properties,
        );
    });
}

/**
 * Converts shared polyline props to Azure Maps features.
 */
export function convertPolylinesToAzure(polylines: PolylineProps[] = []): atlas.data.Feature<atlas.data.LineString, any>[] {
    return polylines
        .filter(polyline => Array.isArray(polyline.locations) && polyline.locations.length > 1)
        .map((polyline) => {
        const azureCoords = convertCoordinatesArray(polyline.locations);
        const line = new atlas.data.LineString(azureCoords);

        const properties: any = {
            strokeColor: polyline.options?.strokeColor,
            strokeWidth: polyline.options?.strokeThickness,
            strokeOpacity: polyline.options?.strokeOpacity,
            ...(polyline.metadata ?? {}),
        };

        if (polyline.eventHandlers && polyline.eventHandlers.length > 0) {
            polyline.eventHandlers.forEach(handler => {
                properties[`on${handler.event}`] = handler.callback;
            });
        }

        return new atlas.data.Feature(line, properties);
        });
}

/**
 * Converts shared polygon props to Azure Maps features.
 */
export function convertPolygonsToAzure(polygons: PolygonProps[] = []): atlas.data.Feature<atlas.data.Polygon, any>[] {
    return polygons.map((polygon) => {
        const azureRings = convertCoordinatesArrays(polygon.rings);
        const azurePolygon = new atlas.data.Polygon(azureRings);

        const properties: any = {
            fillColor: polygon.options?.fillColor,
            strokeColor: polygon.options?.strokeColor,
            strokeWidth: polygon.options?.strokeThickness,
            ...(polygon as any).metadata ?? {},
        };

        if (polygon.eventHandlers && polygon.eventHandlers.length > 0) {
            polygon.eventHandlers.forEach(handler => {
                properties[`on${handler.event}`] = handler.callback;
            });
        }

        return new atlas.data.Feature(azurePolygon, properties);
    });
}

/**
 * Converts shared infobox props to a format suitable for Azure Maps popups.
 */
export function convertInfoboxesToAzure(infoboxes: InfoboxProps[] = []) {
    return infoboxes.map((infobox) => {
        const position = convertCoordinates(infobox.location);
        const title = infobox.title ?? infobox.options?.title;
        const content = infobox.text ?? infobox.options?.description ?? '';

        const options: any = {
            position,
            pixelOffset: infobox.options?.offset ?? [0, -20],
            closeButton: infobox.options?.showCloseButton ?? false,
            title,
            description: typeof content === 'string' ? content : undefined,
            visible: infobox.options?.visible ?? true,
            popupAccentColor: infobox.options?.popupAccentColor,
        };
        const eventHandlers = infobox.eventHandlers || [];

        return {
            options,
            content,
            eventHandlers,
            htmlContent: infobox.options?.htmlContent,
            title,
            visible: infobox.options?.visible ?? true,
        };
    });
}

/**
 * Converts the shared map contract to Azure Maps primitives.
 */
export function convertMapComponentPropsToAzure(mapPropsContract: {
    map?: MapProps;
    pushpins?: PushpinProps[];
    polylines?: PolylineProps[];
    polygons?: PolygonProps[];
    infoboxes?: InfoboxProps[];
}) {
    return {
        mapOptions: convertMapProps(mapPropsContract.map),
        mapEvents: convertMapEventHandlers(mapPropsContract.map?.eventHandlers),
        pushpins: convertPushpinsToAzure(mapPropsContract.pushpins),
        polylines: convertPolylinesToAzure(mapPropsContract.polylines),
        polygons: convertPolygonsToAzure(mapPropsContract.polygons),
        infoboxes: convertInfoboxesToAzure(mapPropsContract.infoboxes),
    };
}
